import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { NumPad } from "@/components/common/NumPad";
import { fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";

const QtyDisplay = ({ value, unit, testId }) => (
  <div data-testid={testId} className="h-16 flex items-center justify-center gap-2 border border-border bg-input rounded-sm font-mono text-4xl font-bold">
    {value || <span className="text-muted-foreground text-lg font-normal">0</span>}<span className="text-base text-muted-foreground font-normal">{unit}</span>
  </div>
);

export const ProductionModal = ({ open, onClose, onSubmit, unit, decimals, busy }) => {
  const [qte, setQte] = useState("");
  const valid = Number(qte) > 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm rounded-sm" data-testid="production-modal">
        <DialogHeader><DialogTitle className="text-base">Déclarer des pièces bonnes</DialogTitle></DialogHeader>
        <QtyDisplay value={qte} unit={unit} testId="production-qty" />
        <NumPad value={qte} onChange={setQte} decimals={decimals} testId="production-numpad" />
        <Button className="h-14 text-lg font-bold bg-emerald-600 hover:bg-emerald-500" disabled={!valid || busy} data-testid="production-submit"
          onClick={async () => { await onSubmit(Number(qte)); setQte(""); }}>Valider + {qte || 0} {unit}</Button>
      </DialogContent>
    </Dialog>
  );
};

export const ArretModal = ({ open, onClose, onSubmit, categories, busy }) => {
  const [comment, setComment] = useState("");
  const nonPlan = categories.filter((c) => c.actif && !c.planifie);
  const plan = categories.filter((c) => c.actif && c.planifie);
  const Group = ({ title, items, planifie }) => (
    <div>
      <div className="eyebrow mb-1.5">{title}</div>
      <div className="grid grid-cols-2 gap-1.5">
        {items.map((c) => (
          <button key={c.id} type="button" disabled={busy} data-testid={`arret-cat-${c.id}`} onClick={async () => { await onSubmit(c.id, comment); setComment(""); }}
            className={cn("h-12 px-2 rounded-sm border text-sm font-semibold text-left truncate transition-colors active:scale-[0.98]",
              planifie ? "bg-amber-950/40 border-amber-600/40 text-amber-200 hover:bg-amber-900/50" : "bg-red-950/40 border-red-600/40 text-red-200 hover:bg-red-900/50")}>{c.nom}</button>
        ))}
      </div>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-auto" data-testid="arret-modal">
        <DialogHeader><DialogTitle className="text-base">Déclarer un arrêt — cause</DialogTitle></DialogHeader>
        <input data-testid="arret-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire (optionnel)" className="h-10 bg-input border border-border rounded-sm px-3 text-sm" />
        <Group title="Non planifiés — pénalisent la disponibilité" items={nonPlan} planifie={false} />
        <Group title="Planifiés — n'affectent pas la disponibilité" items={plan} planifie />
      </DialogContent>
    </Dialog>
  );
};

export const RejetModal = ({ open, onClose, onSubmit, motifs, unit, decimals, busy }) => {
  const [motif, setMotif] = useState(null);
  const [qte, setQte] = useState("");
  const close = () => { setMotif(null); setQte(""); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-sm rounded-sm" data-testid="rejet-modal">
        <DialogHeader><DialogTitle className="text-base">{motif ? `Rejet — ${motif.nom}` : "Déclarer un rejet — motif"}</DialogTitle></DialogHeader>
        {!motif ? (
          <div className="grid gap-1.5">
            {motifs.filter((m) => m.actif).map((m) => (
              <button key={m.id} type="button" data-testid={`rejet-motif-${m.id}`} onClick={() => setMotif(m)}
                className="h-12 px-3 rounded-sm border border-orange-600/40 bg-orange-950/40 text-orange-200 text-sm font-semibold text-left hover:bg-orange-900/50 active:scale-[0.98]">{m.nom}</button>
            ))}
          </div>
        ) : (<>
          <QtyDisplay value={qte} unit={unit} testId="rejet-qty" />
          <NumPad value={qte} onChange={setQte} decimals={decimals} testId="rejet-numpad" />
          <div className="flex gap-2">
            <Button variant="outline" className="h-12" onClick={() => setMotif(null)} data-testid="rejet-back">Motif</Button>
            <Button className="flex-1 h-12 text-base font-bold bg-orange-600 hover:bg-orange-500" disabled={!(Number(qte) > 0) || busy} data-testid="rejet-submit"
              onClick={async () => { await onSubmit(motif.id, Number(qte)); setMotif(null); setQte(""); }}>Valider ×{fmtNum(Number(qte) || 0, decimals)} {unit}</Button>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};

export const ReleveModal = ({ open, onClose, onSubmit, quarts, of, busy }) => {
  const [f, setF] = useState({ quart_id: "", equipe: "", superviseur: "", commentaire: "" });
  useEffect(() => { if (open && of) setF({ quart_id: String(of.quart_id || ""), equipe: of.equipe || "", superviseur: of.superviseur || "", commentaire: "" }); }, [open, of]);
  const inp = "h-11 w-full bg-input border border-border rounded-sm px-3 text-sm";
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="releve-modal">
        <DialogHeader><DialogTitle className="text-base">Relève d'équipe — passer la main</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Le TRS par quart est calculé automatiquement par horodatage ; la relève trace le changement d'équipe et de responsable sur l'OF <span className="font-mono text-foreground">{of?.numero}</span>.</p>
        <label className="space-y-1"><span className="eyebrow">Quart</span>
          <select data-testid="releve-quart" className={inp} value={f.quart_id} onChange={set("quart_id")}>
            {quarts.map((q) => <option key={q.id} value={q.id}>{q.nom} · {q.heure_debut}–{q.heure_fin}</option>)}
          </select></label>
        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-1"><span className="eyebrow">Équipe</span><input data-testid="releve-equipe" className={inp} value={f.equipe} onChange={set("equipe")} placeholder="Ex. Équipe B" /></label>
          <label className="space-y-1"><span className="eyebrow">Superviseur</span><input data-testid="releve-superviseur" className={inp} value={f.superviseur} onChange={set("superviseur")} placeholder="Nom du responsable" /></label>
        </div>
        <input data-testid="releve-commentaire" className={inp} value={f.commentaire} onChange={set("commentaire")} placeholder="Commentaire (optionnel)" />
        <Button className="h-14 text-lg font-bold bg-sky-600 hover:bg-sky-500" disabled={busy || !f.quart_id} data-testid="releve-submit"
          onClick={() => onSubmit({ quart_id: Number(f.quart_id), equipe: f.equipe, superviseur: f.superviseur, commentaire: f.commentaire })}>Valider la relève</Button>
      </DialogContent>
    </Dialog>
  );
};

export const ConfirmModal = ({ open, onClose, onConfirm, title, text, confirmLabel = "Confirmer", tone = "default", busy }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-sm rounded-sm" data-testid="confirm-modal" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <DialogHeader><DialogTitle className="text-base">{title}</DialogTitle></DialogHeader>
      <p className="text-sm text-muted-foreground">{text}</p>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onClose} data-testid="confirm-cancel">Annuler</Button>
        <Button variant={tone === "danger" ? "destructive" : "default"} disabled={busy} onClick={onConfirm} data-testid="confirm-ok">{confirmLabel}</Button>
      </div>
    </DialogContent>
  </Dialog>
);
